import { exhaustiveSwitch } from "replo-utils/lib/misc";
import * as z from "zod";

import { paymentProviderAccountSchema } from "./paymentProvider";

export const connectionUiSchema = z
  .enum([
    "nango-oauth",
    "nango-connect-ui",
    "custom-api-key",
    "shopify-app-install",
    "messages",
    /**
     * Replo is connected *into* an external MCP client (Claude, ChatGPT) rather
     * than the client being connected into Replo. The grant lives on the
     * vendor's side as a public-MCP OAuth grant, so there is no
     * AgentIntegrationConnection row and no Replo-held credential.
     */
    "external-mcp-client",
  ])
  .describe("ConnectionUi");

export type ExternalAccountPolicy = "exclusive" | "shared";

export type ExternalAccountProvider = "slack" | "figma";

export function isExternalAccountProvider(
  integrationKey: string,
): integrationKey is ExternalAccountProvider {
  return integrationKey === "slack" || integrationKey === "figma";
}

export function getExternalAccountPolicy(
  integrationKey: ExternalAccountProvider,
): ExternalAccountPolicy;
export function getExternalAccountPolicy(
  integrationKey: string,
): ExternalAccountPolicy | null;
export function getExternalAccountPolicy(
  integrationKey: string,
): ExternalAccountPolicy | null {
  if (!isExternalAccountProvider(integrationKey)) {
    return null;
  }
  return exhaustiveSwitch(integrationKey)<ExternalAccountPolicy>({
    slack: "exclusive",
    figma: "shared",
  });
}

export function getConnectionStatus({
  connections,
}: {
  connections: { needsReconnect?: boolean }[];
}) {
  if (connections.length === 0) {
    return "not_connected";
  }
  return connections.every((connection) => connection.needsReconnect)
    ? "needs_reconnect"
    : "connected";
}

/**
 * Individual scope key for agent integration connections.
 * Determines which entity dimension(s) an integration is bound to.
 */
export const integrationScopeKeySchema = z
  .enum(["user", "project", "workspace"])
  .describe("IntegrationScopeKey");

/**
 * Last-known health of an agent integration connection. `needs_reconnect`
 * means the stored connection still exists in our database but the upstream
 * provider (e.g. Nango) rejected it as unknown/invalid, so the user must
 * reconnect it before the agent can use its tools again.
 */
// `pending_verification`: the row exists but the operator has not yet proven
// the identity it will be bound to (Messages waits for a text from the phone).
export const integrationConnectionHealthSchema = z
  .enum(["healthy", "needs_reconnect", "pending_verification"])
  .describe("IntegrationConnectionHealth");

// NOTE (Max, 2026-07-24): lives here, not in agentTools/integrationTools, so
// leaf consumers (the bedrock MCP schema graph) don't drag the full
// integration-definitions registry (~12 MB retained heap) for one string schema.
export const integrationConnectionIdSchema = z.string().min(1);

export const integrationAllowedScopesSchema = z
  .array(integrationScopeKeySchema)
  .nonempty()
  .describe("IntegrationAllowedScopes");

export type IntegrationAllowedScopes = z.infer<
  typeof integrationAllowedScopesSchema
>;

export const integrationTargetBindingSchema = z
  .object({
    resourceType: z.string().min(1),
    resourceId: z.string().min(1),
    displayName: z.string().min(1),
    // The owner this resource sits under (a Meta business, a GA4 account), so
    // the picker can bucket by it instead of repeating the owner on each row.
    // Resolved from a live discovery call and never persisted on a binding:
    // providers hand back short-lived signed URLs for `imageUrl`.
    group: z
      .object({
        name: z.string().min(1),
        imageUrl: z.string().min(1).optional(),
      })
      .optional(),
  })
  .describe("IntegrationTargetBinding");

export type IntegrationTargetBinding = z.infer<
  typeof integrationTargetBindingSchema
>;

/**
 * Integration scope determines which entities the integration is connected to.
 * Each key set to true means that dimension is part of the scope (at least one required).
 *
 * Examples:
 * - { user: true } → One connection per user (global to that user across all projects)
 * - { project: true } → One connection per project (shared by all users in the project)
 * - { workspace: true } → One connection per workspace (shared by all users/projects)
 * - { user: true, project: true } → One connection per user per project
 */
export const integrationScopeSchema = z
  .object({
    user: z.literal(true).optional(),
    project: z.literal(true).optional(),
    workspace: z.literal(true).optional(),
  })
  .refine(
    (scope) => {
      return (
        scope.user === true ||
        scope.project === true ||
        scope.workspace === true
      );
    },
    { message: "At least one scope key required" },
  )
  .describe("IntegrationScope");

/**
 * `encryptedShopifyAccessToken` is Fernet ciphertext (keyed off
 * `ENCRYPT_KEY`) base64-encoded so it can live in a JSON column. Runtime
 * readers decode back to a `Uint8Array` before decrypting.
 */
export const shopifyAgentCustomConfigSchema = z
  .object({
    shopifyUrl: z.string(),
    shopName: z.string(),
    primaryDomain: z.string(),
    encryptedShopifyAccessToken: z.string(),
    storefrontAccessToken: z.string(),
    // Defaults to null because connections made before private tokens have no key at all.
    privateStorefrontAccessToken: z.string().nullable().default(null),
    scopes: z.array(z.string()),
    webPixelId: z.string().nullable(),
    installationNonce: z.string().nullable(),
  })
  .describe("ShopifyAgentCustomConfig");

export const agentCommerceConfigSchema = z
  .object({
    shopify: shopifyAgentCustomConfigSchema.nullable(),
    stripe: paymentProviderAccountSchema.nullable(),
  })
  .describe("AgentCommerceConfig");

// `"none"` means checkout is disabled (no provider connected, or the user
// explicitly turned it off) — it is not a sentinel for "unknown".
export const projectCheckoutProviderSchema = z
  .enum(["shopify", "stripe", "none"])
  .describe("ProjectCheckoutProvider");

/** The Home workflow card open in the agent shell. */
export const HOME_WORKFLOW_SEARCH_PARAM = "workflow" as const;

/**
 * Key on `agent_integration_connection.custom_config` for the per-space
 * Delivery API token map. Values are `enc:v1:` envelopes (see Contentful
 * `secretConfigPaths: ["deliveryTokensBySpaceId.*"]`).
 */
export const CONTENTFUL_DELIVERY_TOKENS_BY_SPACE_ID_KEY =
  "deliveryTokensBySpaceId" as const;

export const contentfulDeliveryTokensBySpaceIdSchema = z.record(
  z.string().min(1),
  z.string().min(1),
);

export const contentfulCustomConfigSchema = z.object({
  [CONTENTFUL_DELIVERY_TOKENS_BY_SPACE_ID_KEY]:
    contentfulDeliveryTokensBySpaceIdSchema.optional(),
});

/**
 * Partial Contentful `customConfig` for `updateConnection`. Null map values
 * remove that space's token after merge (lodash merge cannot delete keys).
 */
export const contentfulCustomConfigPatchSchema = z.object({
  [CONTENTFUL_DELIVERY_TOKENS_BY_SPACE_ID_KEY]: z
    .record(z.string().min(1), z.string().min(1).nullable())
    .nullish(),
});
